import { useLazyQuery } from "@apollo/client";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import Banner from "@/components/Banner";
import Button from "@/components/Button";
import SubSectionHeading from "@/components/SubSectionHeading";
import TextField from "@/components/TextField";
import ChapterAutocomplete from "@/features/facility/components/ContactDetails/forms/inputs/ChapterAutocomplete";
import SectionAutocomplete from "@/features/facility/components/ContactDetails/forms/inputs/SectionAutocomplete";

import { CRM_SECTION_BY_POSTAL_CODE_QUERY, FACILITIES_SEARCH_QUERY, type FacilitySearchResult } from "../../queries";
import { type CreateFacilityFormValues } from "../../_utils/createFacilityForm";
import {
  buildSectionSuggestionSearchInputs,
  buildSectionSuggestions,
  normalizeZipCode,
  type SectionSuggestion,
  shouldShowAlternateSectionSuggestion
} from "../../_utils/facilitySectionSuggestion";

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatAddress(address: FacilitySearchResult["mainAddress"]) {
  if (!address) return "No address on file";

  return [address.address1, address.city, address.state, address.postalCode].filter(Boolean).join(", ");
}

function suggestionReason(suggestion: SectionSuggestion, lookupZip: string) {
  const nearbyCount = suggestion.facilityCount - suggestion.exactZipCount;

  if (suggestion.source === "postal_mapping") {
    return `PGA Backend maps ${lookupZip} to this section. No sampled facilities were found to confirm it.`;
  }

  if (suggestion.source === "postal_mapping_with_facility_evidence") {
    if (suggestion.exactZipCount > 0) {
      return [
        `PGA Backend maps ${lookupZip} to this section.`,
        `Found ${pluralize(suggestion.exactZipCount, "facility", "facilities")} in ${lookupZip} confirming it.`,
        nearbyCount > 0
          ? `${pluralize(nearbyCount, "nearby facility", "nearby facilities")} also matched this section.`
          : null
      ]
        .filter(Boolean)
        .join(" ");
    }

    return `PGA Backend maps ${lookupZip} to this section. The closest sampled facility for this section is in ${suggestion.closestZip}.`;
  }

  if (suggestion.exactZipCount > 0) {
    return [
      `Found ${pluralize(suggestion.exactZipCount, "facility", "facilities")} in ${lookupZip}.`,
      nearbyCount > 0
        ? `${pluralize(nearbyCount, "nearby facility", "nearby facilities")} also matched this section.`
        : null
    ]
      .filter(Boolean)
      .join(" ");
  }

  if (suggestion.closestZipDistance <= 2) {
    return `No exact ZIP match found. The closest sampled facility for this section is in ${suggestion.closestZip}.`;
  }

  return `No exact or adjacent ZIP match found. This option comes from facilities in the ${lookupZip.slice(
    0,
    3
  )} ZIP prefix.`;
}

function sectionSuggestionLabel(suggestion: SectionSuggestion, index: number) {
  if (index > 0) return "Possible Alternate Near a Boundary";

  return suggestion.source === "facility_evidence" ? "Best Suggestion" : "Mapped Section";
}

function SectionSuggestionCard({
  label,
  suggestion,
  lookupZip,
  onApply
}: {
  label: string;
  suggestion: SectionSuggestion;
  lookupZip: string;
  onApply: () => void;
}) {
  return (
    <div className="rounded-lg border border-sys-brand-outline-variant p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <SubSectionHeading>{label}</SubSectionHeading>
          <p className="text-title-medium text-sys-brand-on-surface">{suggestion.section.name}</p>
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {suggestionReason(suggestion, lookupZip)}
          </p>
        </div>
        <Button type="button" variant="outlined" onClick={onApply}>
          Use this Section
        </Button>
      </div>
      <ul className="mt-3 flex flex-col gap-1 text-body-small text-sys-brand-on-surface-variant">
        {suggestion.examples.map((facility) => (
          <li key={facility.id}>
            <a
              href={`/facilities/${facility.id}/details/contact?return_to=/facilities/new`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {facility.name}
            </a>
            {` - ${formatAddress(facility.mainAddress)}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

const SectionZipLookup = memo(function SectionZipLookup({ initialPostalCode }: { initialPostalCode?: string | null }) {
  const [findSectionFacilities, { loading: sectionSuggestionLoading }] = useLazyQuery(FACILITIES_SEARCH_QUERY, {
    fetchPolicy: "network-only"
  });
  const [findMappedSection, { loading: mappedSectionLoading }] = useLazyQuery(CRM_SECTION_BY_POSTAL_CODE_QUERY, {
    fetchPolicy: "network-only"
  });
  const { control, setValue } = useFormContext<CreateFacilityFormValues>();
  const { isSubmitting } = useFormState({ control });
  const mainAddressPostalCode = useWatch({ control, name: "mainAddress.postalCode" });
  const [sectionLookupZip, setSectionLookupZip] = useState(() => normalizeZipCode(initialPostalCode));
  const [sectionLookupError, setSectionLookupError] = useState("");
  const [sectionLookupSearchedZip, setSectionLookupSearchedZip] = useState("");
  const [sectionSuggestions, setSectionSuggestions] = useState<SectionSuggestion[]>([]);
  const normalizedMainAddressZip = normalizeZipCode(mainAddressPostalCode);
  const normalizedSectionLookupZip = normalizeZipCode(sectionLookupZip);
  const sectionLookupLoading = sectionSuggestionLoading || mappedSectionLoading;
  const visibleSectionSuggestions = sectionSuggestions.slice(
    0,
    shouldShowAlternateSectionSuggestion(sectionSuggestions) ? 2 : 1
  );

  useEffect(() => {
    if (!normalizedMainAddressZip || sectionLookupZip) return;

    setSectionLookupZip(normalizedMainAddressZip);
  }, [normalizedMainAddressZip, sectionLookupZip]);

  const findSuggestedSections = async () => {
    const lookupZip = normalizeZipCode(sectionLookupZip);
    setSectionLookupError("");
    setSectionLookupSearchedZip("");
    setSectionSuggestions([]);

    if (!lookupZip) {
      setSectionLookupError("Enter a 5-digit ZIP code to look up section suggestions.");
      return;
    }

    try {
      const mappedSectionResult = await findMappedSection({
        variables: {
          postalCode: lookupZip
        }
      });
      const results = await Promise.all(
        buildSectionSuggestionSearchInputs(lookupZip).map((input) =>
          findSectionFacilities({
            variables: {
              first: 10,
              input
            }
          })
        )
      );
      const suggestions = buildSectionSuggestions({
        zipCode: lookupZip,
        facilities: results.flatMap((result) => result.data?.facilities.nodes || []),
        mappedSection: mappedSectionResult.data?.crmSectionByPostalCode
      });

      setSectionLookupSearchedZip(lookupZip);
      setSectionSuggestions(suggestions);
    } catch {
      setSectionLookupError("Section suggestions could not be loaded. Choose the section manually.");
    }
  };

  const applySectionSuggestion = (suggestion: SectionSuggestion) => {
    setValue("sectionId", suggestion.section.id, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-4">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <SubSectionHeading>Section ZIP Lookup</SubSectionHeading>
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            Enter a ZIP code to check the PGA section mapping and compare it with facilities in that ZIP and nearby
            ZIPs. PGA section boundaries can split a ZIP code, so confirm the selected section before saving.
          </p>
        </div>
        <div className="flex flex-col gap-6 md:flex-row md:items-center">
          <TextField
            className="w-full md:max-w-64"
            label="Lookup ZIP"
            value={sectionLookupZip}
            onChangeText={(value) => {
              setSectionLookupZip(value);
              setSectionLookupError("");
            }}
            disabled={isSubmitting || sectionLookupLoading}
            error={!!sectionLookupError}
            errorText={sectionLookupError}
          />
          <Button
            type="button"
            variant="outlined"
            onClick={findSuggestedSections}
            disabled={isSubmitting || sectionLookupLoading}
            iconClassName={classNames({
              "[&>md-icon]:animate-spin": sectionLookupLoading
            })}
            icon={sectionLookupLoading ? "progress_activity" : "search"}
          >
            {sectionLookupLoading ? "Finding" : "Find Section"}
          </Button>
          {normalizedMainAddressZip && normalizedMainAddressZip !== normalizedSectionLookupZip && (
            <Button
              type="button"
              variant="text"
              onClick={() => {
                setSectionLookupZip(normalizedMainAddressZip);
                setSectionLookupError("");
              }}
              disabled={isSubmitting || sectionLookupLoading}
            >
              Use Main Address ZIP
            </Button>
          )}
        </div>
        {sectionLookupSearchedZip && visibleSectionSuggestions.length === 0 && (
          <Banner
            inline
            variant="info"
            description="No section suggestion found."
            subText={`No postal-code mapping or nearby facilities were found for ${sectionLookupSearchedZip}. Choose the section manually.`}
          />
        )}
        {visibleSectionSuggestions.length > 0 && (
          <div className="flex flex-col gap-6">
            {visibleSectionSuggestions.map((suggestion, index) => (
              <SectionSuggestionCard
                key={suggestion.section.id}
                label={sectionSuggestionLabel(suggestion, index)}
                suggestion={suggestion}
                lookupZip={sectionLookupSearchedZip}
                onApply={() => applySectionSuggestion(suggestion)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

interface SectionAffiliationStepProps {
  initialPostalCode?: string | null;
}

export default function SectionAffiliationStep({ initialPostalCode }: SectionAffiliationStepProps) {
  const { control } = useFormContext<CreateFacilityFormValues>();
  const selectedSectionId = useWatch({ control, name: "sectionId" });

  return (
    <div className="flex flex-col gap-6">
      <p className="text-title-large mb-2">Confirm the PGA section affiliation for this facility.</p>
      <SectionZipLookup initialPostalCode={initialPostalCode} />
      <SectionAutocomplete
        key={selectedSectionId || "empty-section"}
        name="sectionId"
        label="Section Name"
        initialQuery=""
        required
      />
      <ChapterAutocomplete name="chapterId" label="Section Chapter Name" sectionId={selectedSectionId || undefined} />
    </div>
  );
}
